import { BlockEditProps, registerBlockType } from "@wordpress/blocks";
import metadata from "./block.json";
import {
  BlockControls,
  InspectorControls,
  useBlockProps,
} from "@wordpress/block-editor";
import { useCallback, useEffect, useMemo, useState } from "react";
import BuilderPage from "./components/builder";
import TablePage from "./components/table";
import PickerPage from "./components/picker";
import Recovery from "./components/recovery";
import { Button, PanelBody, ToolbarButton } from "@wordpress/components";
import { isNumeric } from "@wptb/utils/check";
import { WPTBLogo } from "@wptb/ui/icons";
import type { CorruptionInfo, SaveTableResponse } from "@wptb/dev/types";
import store, { startBuilder, setTable as setTableAction } from "@wptb/external/store";
import { TimeTravel, parseTable, notify, getRepairedTableHtml } from "@wptb/external/utils";

interface WPTBBlockProps {
  id?: string;
}

const Edit = ({
  attributes,
  setAttributes,
}: BlockEditProps<WPTBBlockProps>) => {
  const blockProps = useBlockProps({
    className: "wptb-gutenberg",
  });
  const [shouldLoad, setShouldLoad] = useState(isNumeric(attributes.id));
  const [builder, setBuilder] = useState(false);
  const [tblId, setTblId] = useState(attributes.id);
  const [saveId, setSaveId] = useState(0);
  const [corruption, setCorruption] = useState<CorruptionInfo | undefined>();
  const [rawTableHtml, setRawTableHtml] = useState<string | undefined>();

  useEffect(() => {
    setTblId(attributes.id);
  }, [attributes.id]);

  const setTable = (id: string, isNew?: boolean) => {
    if (id === attributes.id) {
      setTblId(id);
      return;
    }
    if (isNew) {
      setBuilder(true);
      setTblId(id);
      setShouldLoad(false);
    } else {
      setShouldLoad(true);
      setAttributes({ id });
    }
  };

  const onSave = useCallback(
    (data: SaveTableResponse) => {
      if (data.is_new) {
        const id = data.id.toString();
        setTblId(id);
        setAttributes({ id });
      }
      setSaveId((prev) => prev + 1);
    },
    [tblId]
  );
  
  const startBuilderCallback = useCallback(() => {
    if (!tblId) {
      return;
    }
    setBuilder(true);
    store.dispatch(startBuilder(tblId));
    TimeTravel.init();
  }, [tblId]);

  const handleCorruptionChange = useCallback((corruptionInfo: CorruptionInfo | undefined, rawHtml: string | undefined) => {
    setCorruption(corruptionInfo);
    setRawTableHtml(rawHtml);
  }, []);

  const handleRecover = useCallback(() => {
    if (!rawTableHtml || !tblId) return;
    
    const repairedHtml = getRepairedTableHtml(rawTableHtml);
    const parsed = parseTable(repairedHtml)!;
    const { table, props, innerBlocks, tableProps } = parsed;

    store.dispatch(
      setTableAction({
        id: tblId,
        table: {
          id: tblId,
          title: "",
          desktop: table.desktop,
          tablet: table.tablet,
          mobile: table.mobile,
        },
        tableProps,
        props,
        innerBlocks,
      })
    );
    store.dispatch(startBuilder(tblId));
    TimeTravel.init();
    
    setCorruption(undefined);
    setRawTableHtml(undefined);
    setSaveId((prev) => prev + 1); // Trigger table re-render
    notify("Table repaired successfully", "success", 3000);
  }, [rawTableHtml, tblId]);

  const handleCancelRecovery = useCallback(() => {
    setCorruption(undefined);
    setRawTableHtml(undefined);
    setBuilder(false);
    setTblId("");
  }, []);

  const builderComponent = useMemo(() => {
    if (!tblId || !builder || corruption) {
      return null;
    }
    return (
      <BuilderPage
        onClose={() => setBuilder(false)}
        onSave={onSave}
      />
    );
  }, [builder, tblId, onSave, corruption]);

  return (
    <div {...blockProps}>
      {corruption && tblId ? (
        <Recovery
          corruption={corruption}
          onRecover={handleRecover}
          onCancel={handleCancelRecovery}
        />
      ) : tblId ? (
        <TablePage
          key={tblId}
          id={tblId}
          saveId={saveId}
          shouldLoad={shouldLoad}
          setShouldLoad={setShouldLoad}
          onCorruptionChange={handleCorruptionChange}
        />
      ) : (
        <PickerPage setTable={setTable} id={attributes.id} />
      )}
      {builderComponent}
      <InspectorControls>
        <PanelBody title="WP Table Builder" initialOpen>
          <Button
            variant="secondary"
            onClick={() => {
              setTblId("");
              setCorruption(undefined);
              setRawTableHtml(undefined);
            }}
            icon="remove"
            className="wptb-show-picker-btn"
          >
            Change Table
          </Button>

          <Button
            variant="primary"
            onClick={startBuilderCallback}
            icon="share-alt2"
            className="wptb-open-editor-btn"
            disabled={!!corruption}
          >
            Edit Table
          </Button>
        </PanelBody>
      </InspectorControls>
      <BlockControls>
        <ToolbarButton
          icon="share-alt2"
          label="Edit Table"
          onClick={startBuilderCallback}
          placeholder="Edit Table"
          disabled={!!corruption}
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        />
      </BlockControls>
    </div>
  );
};

const save = () => {
  return null;
};

// @ts-expect-error typical gutenberg
registerBlockType(metadata.name, {
  title: metadata.title,
  category: metadata.category,
  icon: <WPTBLogo />,
  attributes: metadata.attributes,
  edit: Edit,
  save,
  transforms: {
    from: [
      {
        type: "shortcode",
        tag: "wptb",
        attributes: {
          id: {
            type: "number",
            shortcode: ({ named }: any) => {
              return parseInt(named.id, 10);
            },
          },
        },
        priority: 1,
      },
    ],
  },
});
